import { useEffect, useState, useCallback  } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "./api";
import useIdleLogout from "./useIdleLogout";
import FooterLogo from "./FooterLogo";

export default function PatientList() {
  const [patients, setPatients] = useState([]);
  const [search, setSearch] = useState("");
  const [filterDate, setFilterDate] = useState("");
  const [followupOnly, setFollowupOnly] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState([]);
  const navigate = useNavigate();

  const columns = {
    request_number: "Request Number",
    follow_up: "Follow-up?",
    rec_cre_dte: "Create Date",
    patient_identification_number: "Patient ID",
    patient_surname: "Patient Surname",
    patient_full_names: "Patient Full Names",
    patient_cell_number: "Patient Cell Number",
    patient_email_address: "Patient Email",
    main_medical_scheme: "Medical Scheme",
    main_medical_scheme_plan: "Medical Scheme Plan",
    main_medical_member_number: "Medical Scheme Number",
    main_identification_number: "Main Member ID",
    main_surname: "Main Member Surname",
    main_full_names: "Main Member Full Names",
    main_cell_number: "Main Member Cell",
    main_email_address: "Main Member Email",
  };

  const wideColumns = Object.keys(columns).filter(
    (c) => c !== "follow_up" && c !== "request_number"
  );

  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return isNaN(date)
      ? ""
      : `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(
          2,
          "0"
        )}-${String(date.getUTCDate()).padStart(2, "0")} ${String(
          date.getUTCHours()
        ).padStart(2, "0")}:${String(date.getUTCMinutes()).padStart(2, "0")}`;
  };

  useEffect(() => {
    const token = sessionStorage.getItem("token");
    if (!token) return navigate("/");

    api
      .get(`/patients`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const sorted = res.data.sort(
          (a, b) => b.request_number - a.request_number
        );
        setPatients(sorted);
      })
      .catch((err) => {
        console.error("Failed to load patients", err);
        if (err.response?.status === 401 || err.response?.status === 403)
          navigate("/home");
      });
  }, [navigate]);

  const handleSort = (key, isMultiSort) => {
    setSortConfig((prev) => {
      const existing = prev.find((s) => s.key === key);
      let updated = isMultiSort ? [...prev] : [];
      if (existing) {
        const direction = existing.direction === "asc" ? "desc" : "asc";
        updated = updated.map((s) => (s.key === key ? { ...s, direction } : s));
      } else {
        updated.push({ key, direction: "asc" });
      }
      return updated;
    });
  };

  const filtered = patients.filter(
    (p) =>
      Object.keys(columns).some((key) =>
        String(p[key] || "")
          .toLowerCase()
          .includes(search.toLowerCase())
      ) &&
      (!filterDate ||
        new Date(p.rec_cre_dte).toDateString() ===
          new Date(filterDate).toDateString()) &&
      (!followupOnly || p.follow_up)
  );

  const sortedPatients = [...filtered].sort((a, b) => {
    for (let { key, direction } of sortConfig) {
      const aVal = a[key] || "",
        bVal = b[key] || "";
      if (aVal < bVal) return direction === "asc" ? -1 : 1;
      if (aVal > bVal) return direction === "asc" ? 1 : -1;
    }
    return 0;
  });

  const totalPages =
    rowsPerPage === -1 ? 1 : Math.ceil(filtered.length / rowsPerPage);
  const indexOfLast =
    rowsPerPage === -1 ? filtered.length : currentPage * rowsPerPage;
  const currentPatients = sortedPatients.slice(
    rowsPerPage === -1 ? 0 : indexOfLast - rowsPerPage,
    indexOfLast
  );

  const [loading, setLoading] = useState(false);

  const fetchPatients = useCallback(() => {
    const token = sessionStorage.getItem("token");
    if (!token) return navigate("/");
  
    setLoading(true);
  
    api
      .get(`/patients`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const sorted = res.data.sort(
          (a, b) => b.request_number - a.request_number
        );
        setPatients(sorted);
      })
      .catch((err) => {
        console.error("Failed to load patients", err);
        if (err.response?.status === 401 || err.response?.status === 403)
          navigate("/");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [navigate]);
  

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);
  

  const { showWarning, stayLoggedIn } = useIdleLogout();

  return (
    <div
      className="min-h-screen w-full bg-cover bg-center text-gray-800"
      style={{
        backgroundImage: `radial-gradient(circle at top left, #A8A8A8, #8C8C8C, #85D3DE, #8DDFEB)`,
      }}
    >
      <div className="flex justify-center pt-6">
        <img
          src="/InfoMedi_logo.png"
          alt="InfoMedi Logo"
          className="h-20 drop-shadow"
        />
      </div>

      <div className="bg-white/30 backdrop-blur-md shadow-xl rounded-xl px-6 py-8 mt-8 max-w-7xl mx-auto border border-white/40">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-semibold text-gray-800">
            Patient Records
          </h1>
          <div className="flex gap-4">
            <button
              onClick={fetchPatients}
              className="bg-gradient-to-r from-gray-400 to-gray-700 hover:from-gray-600 hover:to-gray-800 text-white px-4 py-2 rounded-lg shadow-md transition-all"
            >
              REFRESH GRID
            </button>

            <button
              onClick={() => navigate("/form")}
              className="bg-gradient-to-r from-gray-400 to-blue-800 hover:from-blue-600 hover:to-gray-700 text-white px-4 py-2 rounded-lg shadow-md transition-all"
            >
              PATIENT FORM
            </button>
            <button
              onClick={() => navigate("/sig")}
              className="bg-gradient-to-r from-gray-400 to-blue-800 hover:from-blue-600 hover:to-gray-700 text-white px-4 py-2 rounded-lg shadow-md transition-all"
            >
              SIGNATURE CAPTURE
            </button>
            <button
              onClick={() => {
                sessionStorage.removeItem("token");
                navigate("/");
              }}
              className="bg-gradient-to-r from-gray-400 to-gray-700 hover:from-gray-600 hover:to-gray-800 text-white px-4 py-2 rounded-lg shadow-md transition-all"
            >
              LOGOUT
            </button>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 shadow-sm w-full sm:max-w-xs"
          />

        <label className="flex items-center gap-3 cursor-pointer whitespace-nowrap">
          <span className="text-sm font-medium">Follow-up Only</span>

          <button
            type="button"
            onClick={() => {
              setCurrentPage(1);
              setFollowupOnly(!followupOnly);
            }}
            className={`relative inline-flex h-6 w-12 items-center rounded-full transition ${
              followupOnly ? "bg-blue-600" : "bg-gray-400"
            }`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${
                followupOnly ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </label>

          <input
            type="date"
            value={filterDate}
            onChange={(e) => {
              setCurrentPage(1);
              setFilterDate(e.target.value);
            }}
            className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 shadow-sm w-full sm:max-w-xs"
          />

          <select
            value={rowsPerPage}
            onChange={(e) => {
              setCurrentPage(1);
              setRowsPerPage(Number(e.target.value));
            }}
            className="p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 shadow-sm w-full sm:max-w-xs"
          >
            {[10, 20, 50, 100, "All"].map((n) => (
              <option key={n} value={n === "All" ? -1 : n}>
                {n === "All" ? "All" : `${n} rows`}
              </option>
            ))}
          </select>
        </div>

        <div
          className="w-full overflow-x-auto overflow-y-auto"
          style={{
            height: "600px",
            minHeight: "400px",
            maxHeight: "600px",
            overflowX: "scroll",
            overflowY: "auto",
          }}
        >
          {loading && (
            <div className="text-center text-blue-700 font-semibold mb-4">
              Refreshing patient records...
            </div>
          )}

          <table className="min-w-[1600px] w-full text-sm table-auto border border-gray-200 rounded-lg">
            <thead className="bg-[#f0f0f0] text-gray-700 sticky top-0 z-10 text-xs">
              <tr>
                {Object.keys(columns).map((key) => {
                  const sort = sortConfig.find((s) => s.key === key);
                  return (
                    <th
                      key={key}
                      onClick={(e) => handleSort(key, e.shiftKey)}
                      className={`px-4 py-2 font-semibold border-b cursor-pointer text-left ${
                          key === "follow_up"
                            ? "min-w-[70px]"
                            : key === "request_number"
                            ? "min-w-[125px]"
                            : wideColumns.includes(key)
                            ? "min-w-[180px]"
                            : "min-w-[120px]"
                        }`}
                    >
                      {columns[key]}{" "}
                      {sort ? (sort.direction === "asc" ? "↑" : "↓") : ""}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {currentPatients.map((p, idx) => (
                <tr
                  key={idx}
                  onClick={() =>
                            navigate(
                              p.follow_up
                                ? `/followup-patients/${p.request_number}`
                                : `/patients/${p.request_number}`
                            )
                          }
                  className="cursor-pointer even:bg-[#F0F0F0] bg-[#DBDBDB] hover:bg-[#BABABA]"
                >
                  {Object.keys(columns).map((key) => (
                    <td
                      key={key}
                      className={`px-4 py-1 truncate max-w-[180px] ${
                      key === "follow_up"
                        ? "min-w-[100px]"
                        : wideColumns.includes(key)
                        ? "min-w-[180px]"
                        : "min-w-[120px]"
                    }`}
                    >
                      {key === "rec_cre_dte"
                      ? formatDateTime(p[key])
                      : key === "follow_up"
                      ? (p[key] ? "Yes" : "")
                      : p[key] || ""}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filtered.length > 0 && (
          <div className="text-sm text-gray-600 mt-4 mb-2 text-center">
            Showing {rowsPerPage === -1 ? 1 : indexOfLast - rowsPerPage + 1}–
            {indexOfLast} of {filtered.length} records
          </div>
        )}

        {rowsPerPage !== -1 && (
          <div className="flex justify-center mt-2 space-x-2">
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                onClick={() => setCurrentPage(i + 1)}
                className={`px-3 py-1 rounded-lg ${
                  currentPage === i + 1
                    ? "bg-blue-600 text-white"
                    : "bg-blue-100 text-blue-700 hover:bg-blue-200"
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        )}
      </div>
      {showWarning && (
        <div className="fixed bottom-4 right-4 bg-yellow-100 text-yellow-900 border border-yellow-400 px-4 py-2 rounded shadow-lg z-50">
          <p>You will be logged out due to inactivity in 30 seconds.</p>
          <button
            onClick={stayLoggedIn}
            className="mt-2 px-3 py-1 bg-blue-600 text-white rounded"
          >
            Stay Logged In
          </button>
        </div>
      )}
      <FooterLogo />
    </div>
  );
}
